/**
 * Dumb view for LocationsView. Receives already-fetched locations + loading
 * state from the smart wrapper, owns location-specific UI state (view mode,
 * both Mantine forms) and emits persistence intents back as callback props.
 * Shared grid machinery (filter, selection, counts, pending-first sort) comes
 * from useWorldEntityGrid, and the toolbar + card grid from the
 * WorldEntityShell primitives.
 *
 * Two layouts via a SegmentedControl in the toolbar: **Tree** uses Mantine's
 * Tree to nest locations by `parentLocationId` (regions contain cities, etc.);
 * **Grid** is the shared flat card layout. The view auto-defaults to Tree when
 * the current data has any nesting, otherwise Grid; the DM's manual pick is
 * sticky for the rest of the session.
 */

import { LOCATION_STATUSES, LOCATION_TYPES, type TLocationStatus, type TLocationType } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tree,
  type TreeNodeData,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { modals } from "@mantine/modals"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { z } from "zod"
import type { ILocation, ILocationCreateBody, ILocationEditBody } from "../../../lib/api"
import { VoxStatusPill } from "../../VoxStatusPill"
import { useWorldEntityGrid } from "../../WorldEntityShell/useWorldEntityGrid"
import { WorldEntityGrid } from "../../WorldEntityShell/WorldEntityGrid"
import { WorldEntityToolbar } from "../../WorldEntityShell/WorldEntityToolbar"
import shared from "../../WorldEntityShell/worldEntity.module.css"
import type { IViewProps } from "./interfaces"

interface ILocationFormValues {
  name: string
  aliases: string[]
  type: TLocationType | ""
  parentLocationId: string | ""
  status: TLocationStatus
  description: string
}

const locationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  aliases: z.array(z.string()),
  type: z.union([z.enum([...LOCATION_TYPES] as [TLocationType, ...TLocationType[]]), z.literal("")]),
  parentLocationId: z.string(),
  status: z.enum([...LOCATION_STATUSES] as [TLocationStatus, ...TLocationStatus[]]),
  description: z.string(),
})

const locationIsPending = (loc: ILocation): boolean => loc.pending
const locationName = (loc: ILocation): string => loc.name
const locationId = (loc: ILocation): string => loc.id

function emptyFormValues(): ILocationFormValues {
  return {
    name: "",
    aliases: [],
    type: "",
    parentLocationId: "",
    status: "known",
    description: "",
  }
}

function formValuesFromLocation(loc: ILocation): ILocationFormValues {
  return {
    name: loc.name,
    aliases: loc.aliases,
    type: loc.type ?? "",
    parentLocationId: loc.parentLocationId ?? "",
    status: loc.status,
    description: loc.description ?? "",
  }
}

function buildCreatePayload(v: ILocationFormValues): ILocationCreateBody {
  const out: ILocationCreateBody = { name: v.name.trim(), status: v.status }
  if (v.aliases.length) out.aliases = v.aliases
  if (v.type) out.type = v.type
  if (v.parentLocationId) out.parentLocationId = v.parentLocationId
  if (v.description.trim()) out.description = v.description.trim()
  return out
}

function buildEditPayload(v: ILocationFormValues): ILocationEditBody {
  return {
    name: v.name.trim(),
    aliases: v.aliases,
    type: v.type ? v.type : null,
    parentLocationId: v.parentLocationId ? v.parentLocationId : null,
    status: v.status,
    description: v.description.trim() ? v.description.trim() : null,
  }
}

/**
 * Map a location lifecycle status onto the VoxStatusPill design tone.
 *   visited     → active   (the party has been here)
 *   known       → revealed (on the map but not yet visited)
 *   destroyed   → tension  (sacked, burned, ruined)
 *   lost        → archived (party lost the trail)
 *   unreachable → archived (gated behind some condition)
 */
function statusTone(status: TLocationStatus): "active" | "revealed" | "tension" | "archived" {
  switch (status) {
    case "visited":
      return "active"
    case "known":
      return "revealed"
    case "destroyed":
      return "tension"
    case "lost":
    case "unreachable":
      return "archived"
    default:
      return "archived"
  }
}

export function LocationsViewView({
  locations,
  isLoading,
  initialEntityId,
  initialCreateName,
  isCreating,
  isUpdating,
  isAccepting,
  isDeleting,
  isBulkBusy,
  onCreate,
  onUpdate,
  onAccept,
  onDelete,
  onAcceptAllPending,
  onDeleteAllPending,
}: IViewProps): React.JSX.Element {
  // 1. STATE
  const [viewMode, setViewMode] = useState<"grid" | "tree">("grid")

  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  const {
    filter,
    setFilter,
    selected,
    editing,
    setEditing,
    createModalOpen,
    setCreateModalOpen,
    pendingCount,
    acceptedCount,
    visibleItems: visibleLocations,
    pendingItems: pendingLocations,
    selectDetail,
    closeDrawer,
  } = useWorldEntityGrid<ILocation>({
    items: locations,
    isPending: locationIsPending,
    getName: locationName,
    getId: locationId,
    initialEntityId,
  })

  // Auto-default to tree when the data set has any nesting; once the DM
  // picks a layout we stop overriding it.
  const userPickedView = useRef<boolean>(false)

  // 3. DERIVED STATE
  const nameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of locations) m.set(l.id, l.name)
    return m
  }, [locations])

  // Tree structure built from parent_location_id. Roots are anything whose
  // parent isn't in the visible set (truly top-level OR orphaned by the
  // filter). Children are sorted alphabetically with pending entries first
  // so the DM still sees what needs review at the top of each subtree.
  const treeData = useMemo<TreeNodeData[]>(() => {
    const idSet = new Set(visibleLocations.map((l) => l.id))
    const childrenByParent = new Map<string | null, ILocation[]>()
    for (const loc of visibleLocations) {
      const parent = loc.parentLocationId && idSet.has(loc.parentLocationId) ? loc.parentLocationId : null
      const list = childrenByParent.get(parent) ?? []
      list.push(loc)
      childrenByParent.set(parent, list)
    }
    const sortFn = (a: ILocation, b: ILocation): number => {
      if (a.pending && !b.pending) return -1
      if (!a.pending && b.pending) return 1
      return a.name.localeCompare(b.name)
    }
    const buildNode = (loc: ILocation): TreeNodeData => {
      const kids = (childrenByParent.get(loc.id) ?? []).slice().sort(sortFn)
      return {
        value: loc.id,
        label: loc.name,
        children: kids.length > 0 ? kids.map(buildNode) : undefined,
      }
    }
    const roots = (childrenByParent.get(null) ?? []).slice().sort(sortFn)
    return roots.map(buildNode)
  }, [visibleLocations])

  // Parent options for the form: every location except the one being edited
  // (a location can't be its own parent). Including pending entries so the DM
  // can wire hierarchy as they curate.
  const parentOptions = useMemo(
    () => locations.filter((l) => !selected || l.id !== selected.id).map((l) => ({ value: l.id, label: l.name })),
    [locations, selected],
  )

  // 4. FORMS
  const editForm = useForm<ILocationFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(locationSchema),
  })

  const createForm = useForm<ILocationFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(locationSchema),
  })

  // 5. CALLBACKS
  const handleViewModeChange = useCallback((v: string): void => {
    userPickedView.current = true
    setViewMode(v === "tree" ? "tree" : "grid")
  }, [])

  const handleEditClick = useCallback((): void => {
    if (!selected) return
    editForm.setValues(formValuesFromLocation(selected))
    setEditing(true)
  }, [editForm, selected, setEditing])

  const handleEditSubmit = useCallback(
    async (values: ILocationFormValues): Promise<void> => {
      if (!selected) return
      try {
        await onUpdate(selected.id, buildEditPayload(values))
        setEditing(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep edit mode open so the user's input survives.
      }
    },
    [selected, onUpdate, setEditing],
  )

  const handleCreateSubmit = useCallback(
    async (values: ILocationFormValues): Promise<void> => {
      try {
        await onCreate(buildCreatePayload(values))
        setCreateModalOpen(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep the modal open so the user's input survives.
      }
    },
    [onCreate, setCreateModalOpen],
  )

  const handleAccept = useCallback((): void => {
    if (!selected) return
    void onAccept(selected.id)
  }, [selected, onAccept])

  const handleDelete = useCallback((): void => {
    if (!selected) return
    const id = selected.id
    modals.openConfirmModal({
      title: i18n._(msg`Delete location`),
      children: (
        <Text size="sm">
          <Trans>Are you sure you want to delete this location? This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: async () => {
        try {
          await onDelete(id)
          closeDrawer()
        } catch {
          // Notification handled by smart wrapper; leave the drawer open.
        }
      },
    })
  }, [selected, onDelete, i18n, closeDrawer])

  const handleOpenCreate = useCallback((): void => {
    createForm.setValues(emptyFormValues())
    setCreateModalOpen(true)
  }, [createForm, setCreateModalOpen])

  const handleAcceptAllPending = useCallback((): void => {
    if (pendingLocations.length === 0) return
    modals.openConfirmModal({
      title: i18n._(msg`Accept all pending locations`),
      children: (
        <Text size="sm">
          <Trans>
            Mark all {pendingLocations.length} pending locations as accepted? You can still edit them afterwards.
          </Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Accept all`), cancel: i18n._(msg`Cancel`) },
      onConfirm: () => onAcceptAllPending(),
    })
  }, [pendingLocations, onAcceptAllPending, i18n])

  const handleDeleteAllPending = useCallback((): void => {
    if (pendingLocations.length === 0) return
    modals.openConfirmModal({
      title: i18n._(msg`Delete all pending locations`),
      children: (
        <Text size="sm">
          <Trans>Permanently delete all {pendingLocations.length} pending locations? This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete all`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => onDeleteAllPending(),
    })
  }, [pendingLocations, onDeleteAllPending, i18n])

  // 6. EFFECTS
  useEffect(() => {
    if (userPickedView.current) return
    const hasNesting = locations.some((l) => l.parentLocationId && locations.some((p) => p.id === l.parentLocationId))
    setViewMode(hasNesting ? "tree" : "grid")
  }, [locations])

  // Click-to-resolve from the chronicle (Phase WS-9). See NPCsView for full rationale.
  useEffect(() => {
    if (initialCreateName) {
      const next = emptyFormValues()
      next.name = initialCreateName
      createForm.setValues(next)
      setCreateModalOpen(true)
    }
  }, [initialCreateName, createForm, setCreateModalOpen])

  // 7. GUARD CLAUSES
  if (isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  // 8. RENDER
  return (
    <div className={shared.root}>
      <WorldEntityToolbar
        title={<Trans>Locations</Trans>}
        acceptedCount={acceptedCount}
        pendingCount={pendingCount}
        filter={filter}
        onFilterChange={setFilter}
        bulkBusy={isBulkBusy}
        onAcceptAllPending={handleAcceptAllPending}
        onDeleteAllPending={handleDeleteAllPending}
        addLabel={<Trans>Add location</Trans>}
        onAdd={handleOpenCreate}
        leadingControls={
          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={handleViewModeChange}
            data={[
              { value: "grid", label: i18n._(msg`Grid`) },
              { value: "tree", label: i18n._(msg`Tree`) },
            ]}
          />
        }
      />

      {viewMode === "tree" && visibleLocations.length > 0 ? (
        <Card withBorder padding="md">
          <Tree
            data={treeData}
            levelOffset="lg"
            renderNode={({ node, expanded, hasChildren, elementProps }) => {
              const loc = locations.find((l) => l.id === node.value)
              if (!loc) return <span {...elementProps}>{node.label}</span>
              const handleNodeClick = (e: React.MouseEvent): void => {
                elementProps.onClick(e)
                selectDetail(loc.id)
              }
              const handleNodeKeyDown = (e: React.KeyboardEvent): void => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  selectDetail(loc.id)
                }
              }
              return (
                <button
                  type="button"
                  className={elementProps.className}
                  data-selected={elementProps["data-selected"]}
                  data-value={elementProps["data-value"]}
                  data-hovered={elementProps["data-hovered"]}
                  onClick={handleNodeClick}
                  onKeyDown={handleNodeKeyDown}
                  style={{
                    ...elementProps.style,
                    cursor: "pointer",
                    padding: "4px 6px",
                    background: "transparent",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                  }}
                >
                  <Group gap="xs" wrap="nowrap">
                    {hasChildren && (
                      <Text fz="xs" c="dimmed" style={{ width: 12 }}>
                        {expanded ? "▾" : "▸"}
                      </Text>
                    )}
                    <Text fz="sm" fw={500}>
                      {loc.name}
                    </Text>
                    {loc.type && (
                      <Badge size="xs" color="success" variant="light">
                        {loc.type}
                      </Badge>
                    )}
                    <VoxStatusPill tone={statusTone(loc.status)}>{loc.status}</VoxStatusPill>
                    {loc.pending && (
                      <Badge size="xs" color="warning" variant="filled">
                        <Trans>pending</Trans>
                      </Badge>
                    )}
                  </Group>
                </button>
              )
            }}
          />
        </Card>
      ) : (
        <WorldEntityGrid
          items={visibleLocations}
          emptyMessage={
            filter === "pending" ? (
              <Trans>No pending locations to review.</Trans>
            ) : filter === "accepted" ? (
              <Trans>No accepted locations yet.</Trans>
            ) : (
              <Trans>No locations yet. Run a session or add one manually.</Trans>
            )
          }
          renderCard={(loc) => (
            <Card
              key={loc.id}
              withBorder
              padding="md"
              data-accent="success"
              className={clsx(shared.card, loc.pending && shared.pendingCard)}
              onClick={() => selectDetail(loc.id)}
            >
              <div className={shared.cardHeader}>
                <Text className={shared.cardName} lineClamp={1}>
                  {loc.name}
                </Text>
                {loc.type && (
                  <Badge size="xs" color="success" variant="light">
                    {loc.type}
                  </Badge>
                )}
              </div>
              {loc.parentLocationId && nameById.get(loc.parentLocationId) && (
                <Text fz="sm" c="dimmed">
                  <Trans>in {nameById.get(loc.parentLocationId) ?? ""}</Trans>
                </Text>
              )}
              {loc.description && (
                <Text fz="sm" c="dimmed" lineClamp={2} mt="xs">
                  {loc.description}
                </Text>
              )}
              <div className={shared.cardMeta}>
                <VoxStatusPill tone={statusTone(loc.status)}>{loc.status}</VoxStatusPill>
                {loc.pending && (
                  <Badge size="xs" color="warning" variant="filled">
                    <Trans>pending</Trans>
                  </Badge>
                )}
                {loc.aliases.length > 0 && (
                  <Badge size="xs" color="brand" variant="outline">
                    +{loc.aliases.length} aliases
                  </Badge>
                )}
              </div>
            </Card>
          )}
        />
      )}

      <Drawer
        opened={!!selected}
        onClose={closeDrawer}
        position="right"
        size="md"
        title={selected ? selected.name : ""}
        padding="lg"
      >
        {selected && !editing && (
          <Stack gap="md">
            <Group gap="xs">
              {selected.type && (
                <Badge color="success" variant="light">
                  {selected.type}
                </Badge>
              )}
              <VoxStatusPill tone={statusTone(selected.status)}>{selected.status}</VoxStatusPill>
              {selected.pending && (
                <Badge color="warning" variant="filled">
                  <Trans>pending</Trans>
                </Badge>
              )}
            </Group>

            {selected.parentLocationId && nameById.get(selected.parentLocationId) && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Parent location</Trans>
                </span>
                <Text fz="sm">{nameById.get(selected.parentLocationId)}</Text>
              </div>
            )}

            {selected.aliases.length > 0 && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Aliases</Trans>
                </span>
                <Group gap="xs">
                  {selected.aliases.map((alias) => (
                    <Badge key={alias} variant="outline" color="brand" size="sm">
                      {alias}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {selected.description && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Description</Trans>
                </span>
                <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {selected.description}
                </Text>
              </div>
            )}

            <div className={shared.drawerActions}>
              <Button variant="default" onClick={handleEditClick}>
                <Trans>Edit</Trans>
              </Button>
              {selected.pending && (
                <Button color="success" onClick={handleAccept} loading={isAccepting}>
                  <Trans>Accept</Trans>
                </Button>
              )}
              <Button color="danger" variant="outline" onClick={handleDelete} loading={isDeleting}>
                <Trans>Delete</Trans>
              </Button>
            </div>
          </Stack>
        )}

        {selected && editing && (
          <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
            <Stack gap="md">
              <TextInput label={i18n._(msg`Name`)} key={editForm.key("name")} {...editForm.getInputProps("name")} />
              <TagsInput
                label={i18n._(msg`Aliases`)}
                description={i18n._(msg`Press enter after each alias`)}
                key={editForm.key("aliases")}
                {...editForm.getInputProps("aliases")}
              />
              <Select
                label={i18n._(msg`Type`)}
                clearable
                data={LOCATION_TYPES.map((t) => ({ value: t, label: t }))}
                value={editForm.getValues().type}
                onChange={(v) => editForm.setFieldValue("type", (v ?? "") as TLocationType | "")}
              />
              <Select
                label={i18n._(msg`Parent location`)}
                placeholder={i18n._(msg`(none)`)}
                clearable
                searchable
                data={parentOptions}
                value={editForm.getValues().parentLocationId}
                onChange={(v) => editForm.setFieldValue("parentLocationId", v ?? "")}
              />
              <SegmentedControl
                size="xs"
                fullWidth
                data={LOCATION_STATUSES.map((s) => ({ value: s, label: s }))}
                value={editForm.getValues().status}
                onChange={(v) => editForm.setFieldValue("status", v as TLocationStatus)}
              />
              <Textarea
                label={i18n._(msg`Description`)}
                autosize
                minRows={3}
                key={editForm.key("description")}
                {...editForm.getInputProps("description")}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setEditing(false)}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button type="submit" loading={isUpdating}>
                  <Trans>Save</Trans>
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Drawer>

      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={i18n._(msg`Add location`)}
        size="lg"
      >
        <form onSubmit={createForm.onSubmit(handleCreateSubmit)}>
          <Stack gap="md">
            <TextInput
              label={i18n._(msg`Name`)}
              required
              key={createForm.key("name")}
              {...createForm.getInputProps("name")}
            />
            <TagsInput
              label={i18n._(msg`Aliases`)}
              description={i18n._(msg`Press enter after each alias`)}
              key={createForm.key("aliases")}
              {...createForm.getInputProps("aliases")}
            />
            <Select
              label={i18n._(msg`Type`)}
              clearable
              data={LOCATION_TYPES.map((t) => ({ value: t, label: t }))}
              value={createForm.getValues().type}
              onChange={(v) => createForm.setFieldValue("type", (v ?? "") as TLocationType | "")}
            />
            <Select
              label={i18n._(msg`Parent location`)}
              placeholder={i18n._(msg`(none)`)}
              clearable
              searchable
              data={parentOptions}
              value={createForm.getValues().parentLocationId}
              onChange={(v) => createForm.setFieldValue("parentLocationId", v ?? "")}
            />
            <SegmentedControl
              size="xs"
              fullWidth
              data={LOCATION_STATUSES.map((s) => ({ value: s, label: s }))}
              value={createForm.getValues().status}
              onChange={(v) => createForm.setFieldValue("status", v as TLocationStatus)}
            />
            <Textarea
              label={i18n._(msg`Description`)}
              autosize
              minRows={3}
              key={createForm.key("description")}
              {...createForm.getInputProps("description")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateModalOpen(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" loading={isCreating}>
                <Trans>Create</Trans>
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
